import { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  Button,
  Spinner,
  BrandLockup,
} from "@opero/ui";
import {
  PolarisClient,
  getErrorMessage,
} from "@digiits-agency/polaris-sdk";
import { buildTokenRedirectUrl } from "@opero/shared";
import { API_URL, APP_URL } from "../../../lib/polaris-client";
import { useSetupToken, clearSetupSession } from "../hooks/use-setup-token";
import { OrgProfileSection, type OrgProfileValues } from "../components/OrgProfileSection";
import { InvitationsSection, type InvitationEntry } from "../components/InvitationsSection";
import { ProvisioningProgress } from "../components/ProvisioningProgress";
import { FailedModulesNotice } from "../components/FailedModulesNotice";
import type {
  ProvisionWorkspaceRequest,
  ProvisionWorkspaceResponse,
} from "../types";

export function WorkspaceSetupPage() {
  const navigate = useNavigate();
  const { session, hydrated } = useSetupToken();
  const setupToken = session?.setupToken ?? null;

  const setupClient = useMemo(() => {
    if (!setupToken) return null;
    return new PolarisClient({
      baseUrl: API_URL,
      getToken: () => setupToken,
      productSlug: "opero",
    });
  }, [setupToken]);

  const [org, setOrg] = useState<OrgProfileValues>({
    name: "",
    industry: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    country: "",
  });
  const [invitations, setInvitations] = useState<InvitationEntry[]>([
    { id: crypto.randomUUID(), email: "" },
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProvisionWorkspaceResponse | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  if (!hydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Spinner size="lg" />
      </div>
    );
  }

  // Once provision succeeds (`result` set), keep the user on the success /
  // failed-modules screens even after the setup token expires. Without the
  // `result` clause, the post-provision navigation race re-renders this page
  // with a stale empty token and the early-return <Navigate> beats the
  // explicit navigate to /login?provisioned=1.
  if ((!setupToken || !setupClient) && !result) {
    return <Navigate to="/login?reason=setup-token-missing" replace />;
  }

  // Omit `modules` so the backend installs every licensed module by default.
  const buildRequest = (
    moduleSlugs?: string[],
  ): ProvisionWorkspaceRequest => {
    const validInvites = invitations
      .map((e) => e.email.trim())
      .filter((e) => e && e.includes("@"))
      .map((email) => ({ email }));
    return {
      company: {
        name: org.name.trim(),
        industry: org.industry || undefined,
        timezone: org.timezone || undefined,
        country: org.country.trim() || undefined,
      },
      ...(moduleSlugs ? { modules: moduleSlugs } : {}),
      ...(validInvites.length > 0 ? { invitations: validInvites } : {}),
    };
  };

  // provision-workspace mints a tenant-scoped session in the same call. Hand
  // the tokens to opero-app via the URL hash — the app's auth-context already
  // parses hash tokens on mount.
  const handoffToApp = (res: ProvisionWorkspaceResponse) => {
    if (res.accessToken) {
      window.location.href = buildTokenRedirectUrl(
        APP_URL,
        res.accessToken,
        res.refreshToken ?? "",
      );
      return;
    }
    navigate("/login?provisioned=1", { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!setupClient) {
      navigate("/login?reason=setup-expired", { replace: true });
      return;
    }
    if (!org.name.trim()) {
      setError("Workspace name is required");
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const res = (await setupClient.onboarding.provisionWorkspace({
        body: buildRequest() as unknown as Parameters<
          typeof setupClient.onboarding.provisionWorkspace
        >[0]["body"],
      })) as ProvisionWorkspaceResponse;
      if (res.failedModules?.length > 0) {
        setResult(res);
        setIsSubmitting(false);
      } else {
        // Don't reset isSubmitting — handoffToApp is about to swap the
        // whole document. Re-rendering the form first causes a flash.
        handoffToApp(res);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to set up workspace"));
      setIsSubmitting(false);
    }
  };

  const handleRetryFailed = async () => {
    if (!result) return;
    if (!setupClient) {
      navigate("/login?reason=setup-expired", { replace: true });
      return;
    }
    setIsRetrying(true);
    setError(null);
    try {
      const failedSlugs = result.failedModules.map((f) => f.slug);
      const res = (await setupClient.onboarding.provisionWorkspace({
        body: buildRequest(failedSlugs) as unknown as Parameters<
          typeof setupClient.onboarding.provisionWorkspace
        >[0]["body"],
      })) as ProvisionWorkspaceResponse;
      if (res.failedModules?.length > 0) {
        setResult(res);
        setIsRetrying(false);
      } else {
        // Keep both flags engaged so the failed-modules notice doesn't flash
        // back before the browser navigates.
        setIsSubmitting(true);
        handoffToApp(res);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Retry failed"));
      setIsRetrying(false);
    }
  };

  const continueWithFailed = () => {
    if (!result) return;
    setIsSubmitting(true);
    handoffToApp(result);
  };

  const handleCancel = () => {
    clearSetupSession();
    navigate("/login?reason=setup-token-missing", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <header className="border-b border-slate-200 bg-white px-6 py-4 dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto flex max-w-5xl items-center">
          <BrandLockup />
        </div>
      </header>

      {isSubmitting ? (
        <ProvisioningProgress />
      ) : (
        <main className="mx-auto max-w-3xl px-6 py-10">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Set up your workspace
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Just a few details to get your tenant up and running.
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription className="text-sm text-danger">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {result?.failedModules && result.failedModules.length > 0 && (
            <FailedModulesNotice
              failed={result.failedModules}
              isRetrying={isRetrying}
              onRetry={handleRetryFailed}
              onContinue={continueWithFailed}
            />
          )}

          {!result && (
            <form onSubmit={handleSubmit} className="space-y-10">
              <OrgProfileSection values={org} onChange={setOrg} />
              <InvitationsSection
                entries={invitations}
                onChange={setInvitations}
              />

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Create workspace
                </Button>
              </div>
            </form>
          )}
        </main>
      )}
    </div>
  );
}
